import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, ArrowLeft, CheckCircle2, Square, CheckSquare, Sparkles, ArrowRight } from "lucide-react";
import memberApi from "../../lib/memberApi";
import { PortalNav } from "../../components/portal/PortalNav";

export default function NodeView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [node, setNode] = useState(null);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await memberApi.get(`/member/nodes/${slug}`);
        if (!active) return;
        setNode(data);
        setDone(data.done_items || []);
      } catch (e) {
        if (!active) return;
        setErr(e?.response?.status === 403 ? "locked" : "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      await memberApi.put(`/member/nodes/${slug}/progress`, { done_items: next });
    } finally {
      setSaving(false);
    }
  }, [slug]);

  const toggle = (id) => {
    const next = done.includes(id) ? done.filter((x) => x !== id) : [...done, id];
    setDone(next);
    save(next);
  };

  const items = node?.checklist_items || [];
  const total = items.length;
  const dc = items.filter((i) => done.includes(i.id)).length;
  const isNode04 = slug === "opt-out-of-data-brokers";

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay text-white">
      <PortalNav />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/portal" data-testid="node-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-accent" size={28} /></div>
        ) : err === "locked" ? (
          <div data-testid="node-locked" className="border border-accent/40 bg-[#160606] p-8">
            <h1 className="font-display font-black uppercase text-2xl mb-3">This node is locked</h1>
            <p className="font-body text-sm text-white/60 leading-relaxed mb-6">This node isn't included in your current access. Unlock all 12 nodes plus the bonus chapter with the full pack.</p>
            <a href="/#buy" className="inline-flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors">Get the full pack <ArrowRight size={14} /></a>
          </div>
        ) : err ? (
          <p className="font-body text-white/60">Couldn't load this node. Please try again.</p>
        ) : node ? (
          <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="font-display font-black text-5xl text-white/10">{node.number}</span>
            <h1 data-testid="node-title" className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-2 mb-4">{node.title}</h1>
            <p className="font-body text-white/60 leading-relaxed text-base mb-10">{node.summary}</p>

            <div className="portal-md mb-12" data-testid="node-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node: _n, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2 break-words" />
                  ),
                }}
              >
                {node.markdown || ""}
              </ReactMarkdown>
            </div>

            {total > 0 && (
              <div className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="node-checklist">
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Action checklist</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-accent">{dc}/{total} done {saving && "· saving…"}</span>
                </div>
                <ul className="space-y-3">
                  {items.map((it) => {
                    const checked = done.includes(it.id);
                    return (
                      <li key={it.id}>
                        <button
                          data-testid={`checklist-item-${it.id}`}
                          onClick={() => toggle(it.id)}
                          className="w-full flex items-start gap-3 text-left group"
                        >
                          {checked ? <CheckSquare size={20} className="text-accent shrink-0 mt-0.5" /> : <Square size={20} className="text-white/30 group-hover:text-white/60 shrink-0 mt-0.5 transition-colors" />}
                          <span className={`font-body text-sm leading-relaxed ${checked ? "text-white/40 line-through" : "text-white/80"}`}>{it.text}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {dc === total && (
                  <div className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-400">
                    <CheckCircle2 size={15} /> Node complete
                  </div>
                )}
              </div>
            )}

            {isNode04 && (
              <div data-testid="node4-aura-line" className="mt-8 flex items-center gap-3 border border-white/10 bg-[#0c0c0c] px-5 py-4">
                <Sparkles size={16} className="text-accent shrink-0" />
                <p className="font-body text-sm text-white/60">
                  Don't want to do this by hand?{" "}
                  <Link to="/portal/done-for-you" className="text-accent hover:text-white transition-colors">See the done-for-you option →</Link>
                </p>
              </div>
            )}

            <nav data-testid="node-nav" className="mt-12 grid grid-cols-2 gap-4 border-t border-white/10 pt-8">
              {node.prev ? (
                <Link
                  to={`/portal/node/${node.prev.slug}`}
                  data-testid="node-prev-link"
                  className="group flex flex-col items-start border border-white/10 bg-[#0a0a0a] p-5 hover:border-accent/60 transition-colors"
                >
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                    <ArrowLeft size={13} className="group-hover:-translate-x-1 transition-transform" /> Node {node.prev.number}
                  </span>
                  <span className="font-display font-bold text-sm text-white/80 group-hover:text-white transition-colors">{node.prev.title}</span>
                </Link>
              ) : <div />}
              {node.next ? (
                <Link
                  to={`/portal/node/${node.next.slug}`}
                  data-testid="node-next-link"
                  className="group flex flex-col items-end text-right border border-white/10 bg-[#0a0a0a] p-5 hover:border-accent/60 transition-colors"
                >
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                    Node {node.next.number} <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="font-display font-bold text-sm text-white/80 group-hover:text-white transition-colors">{node.next.title}</span>
                </Link>
              ) : (
                <Link
                  to="/portal"
                  data-testid="node-finish-link"
                  className="group flex flex-col items-end text-right border border-accent/40 bg-[#160606] p-5 hover:border-accent transition-colors"
                >
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-2">
                    Mission map <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="font-display font-bold text-sm text-white/90">Back to dashboard</span>
                </Link>
              )}
            </nav>
          </motion.article>
        ) : null}
      </main>
    </div>
  );
}
